import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useVerification } from "../../context/VerificationContext";
import { useExtractClaims } from "../../hooks/useExtractClaims";
import type { ApiError } from "../../lib/errors";
import { isClaimVerifiable, type ClaimWithSource } from "../../types/claims";
import { ROUTES } from "../../route_paths";

interface UseContentReviewResult {
  isLoading: boolean;
  apiError: ApiError | null;
  rateLimitError: { retryAfter: number } | null;
  rawInput: string;
  /** The claims found that we check; the others are not offered. */
  checkable: ClaimWithSource[];
  /** The claims found that we do not check, which are shown only when none is checkable. */
  unchecked: ClaimWithSource[];
  claims: ClaimWithSource[] | null;
  selected: ClaimWithSource | null;
  setSelected: (claim: ClaimWithSource | null) => void;
  handleVerify: () => void;
}

/**
 * For the URL path: claims arrive pre-extracted in location.state (set by useFetching).
 * For the text path: claims are extracted here on mount.
 * Either way, /review only renders once claims are ready.
 */
export function useContentReview(): UseContentReviewResult {
  const { rawInput, extractedText } = useVerification();
  const location = useLocation();
  const navigate = useNavigate();
  const { execute, isLoading, apiError, rateLimitError } = useExtractClaims();
  const [claims, setClaims] = useState<ClaimWithSource[] | null>(null);
  const [selected, setSelected] = useState<ClaimWithSource | null>(null);
  const hasExtracted = useRef(false);

  const textToExtract = extractedText ?? rawInput;

  // Claims pre-extracted by useFetching (URL path).
  const locationClaims = (location.state as { claims?: ClaimWithSource[] | null } | null)?.claims;

  useEffect(() => {
    if (hasExtracted.current) return;
    hasExtracted.current = true;

    if (locationClaims != null) {
      setClaims(locationClaims);
      const onlyCheckable = locationClaims.filter((c) => isClaimVerifiable(c.properties));
      if (onlyCheckable.length === 1) setSelected(onlyCheckable[0]);
      return;
    }

    if (!textToExtract) {
      navigate(ROUTES.landing.path());
      return;
    }

    execute({ text: textToExtract }).then((result) => {
      if (result !== null) {
        setClaims(result);
        // A claim we do not check is never offered; one that is left goes straight to verification.
        const checkableResult = result.filter((c) => isClaimVerifiable(c.properties));
        const newSelected = checkableResult.length === 1 ? checkableResult[0] : null;
        setSelected(newSelected);

        if (newSelected) {
          // Auto-navigate upon successful extraction of a single claim
          Sentry.addBreadcrumb({
            category: "navigation",
            message: "Single claim extracted, navigating to analysis.",
            level: "info",
            data: { claimId: newSelected.id },
          });
          navigate(ROUTES.analysing.path({ claimId: newSelected.id }), {
            state: { claim: newSelected },
          });
        }
      }
    });
  }, [execute, locationClaims, navigate, textToExtract]);

  function handleVerify() {
    if (!selected || !isClaimVerifiable(selected.properties)) return;
    Sentry.addBreadcrumb({
      category: "navigation",
      message: "Claim selected for verification",
      level: "info",
      data: { claimId: selected.id },
    });
    navigate(ROUTES.analysing.path({ claimId: selected.id }), { state: { claim: selected } });
  }

  const checkable = useMemo(
    () => (claims ?? []).filter((c) => isClaimVerifiable(c.properties)),
    [claims]
  );
  const unchecked = useMemo(
    () => (claims ?? []).filter((c) => !isClaimVerifiable(c.properties)),
    [claims]
  );

  return {
    checkable,
    unchecked,
    isLoading,
    apiError,
    rateLimitError,
    rawInput,
    claims,
    selected,
    setSelected,
    handleVerify,
  };
}
